import { useState, useEffect } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import { getFileDetail, reindexFile, downloadFileContent } from "../../../services/files";
import type { FileDetail } from "../../../services/files";
import { ensurePermission, hasPermission } from "../../../utils/permissions";
import { getAsrStatusLabel, getIndexStatusLabel } from "../../../utils/fileStatus";
import { formatBeijingDate } from "../../../utils/datetime";
import RIcon from "../../../components/RIcon";

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes: number): string {
  if (bytes < 1024) return bytes + " B";
  if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
  return (bytes / 1048576).toFixed(1) + " MB";
}

const permissionLabelMap: Record<string, string> = {
  public: "全公司",
  departments: "指定部门",
  job_titles: "指定工种",
  users: "指定用户",
};


// ─── Index Tree Visualization ─────────────────────────────────────────────────

interface TreeNode {
  id?: string;
  node_id?: string;
  title?: string;
  summary?: string;
  text?: string;
  children?: TreeNode[];
  page_range?: number[];
  pages?: number[];
  level?: number;
  [key: string]: unknown;
}

function TreeNodeCard({ node, depth = 0 }: { node: TreeNode; depth?: number }) {
  const [expanded, setExpanded] = useState(depth < 2);
  const nodeId = node.id || node.node_id || "";
  const title = node.title || nodeId;
  const hasChildren = node.children && node.children.length > 0;
  const textPreview = node.text
    ? node.text.length > 200 ? node.text.slice(0, 200) + "..." : node.text
    : null;
  const pages = node.page_range || node.pages;

  return (
    <div className={`${depth > 0 ? "ml-4 border-l-2 border-gray-100 pl-3" : ""}`}>
      <div
        className="flex items-start gap-2 py-1.5 cursor-pointer group"
        onClick={() => setExpanded(!expanded)}
      >
        {hasChildren ? (
          <i className={`ri-arrow-${expanded ? "down" : "right"}-s-line text-sm text-[#718096] mt-0.5 flex-shrink-0`} />
        ) : (
          <RIcon name="file-text-line" className="text-sm text-[#0EA5A0] mt-0.5 flex-shrink-0" />
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#1A2B3C] group-hover:text-[#0EA5A0] transition-colors">{title}</span>
            {pages && pages.length > 0 && (
              <span className="text-xs bg-gray-100 text-[#718096] px-1.5 py-0.5 rounded">
                p.{pages[0]}{pages.length > 1 ? `-${pages[pages.length - 1]}` : ""}
              </span>
            )}
          </div>
          {node.summary && (
            <p className="text-xs text-[#718096] mt-0.5 line-clamp-2">{node.summary}</p>
          )}
        </div>
      </div>

      {expanded && textPreview && !hasChildren && (
        <div className="ml-6 mb-2 bg-[#F4F8FB] rounded-lg p-2.5">
          <p className="text-xs text-[#4A5568] leading-relaxed whitespace-pre-wrap">{textPreview}</p>
        </div>
      )}

      {expanded && hasChildren && (
        <div className="mt-0.5">
          {node.children!.map((child, i) => (
            <TreeNodeCard key={child.id || child.node_id || i} node={child} depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  );
}

function IndexTreePanel({ treeJson }: { treeJson: string }) {
  let tree: TreeNode | TreeNode[] | null = null;
  let nodeCount = 0;
  let maxDepth = 0;

  try {
    tree = JSON.parse(treeJson);
  } catch {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
        <p className="text-sm text-amber-700">索引树数据格式异常，无法解析</p>
      </div>
    );
  }

  // Count nodes and depth
  function countNodes(n: TreeNode | TreeNode[], depth: number) {
    if (Array.isArray(n)) {
      n.forEach(c => countNodes(c, depth));
      return;
    }
    nodeCount++;
    if (depth > maxDepth) maxDepth = depth;
    if (n.children) n.children.forEach(c => countNodes(c, depth + 1));
  }
  if (tree) countNodes(tree, 0);

  const roots = Array.isArray(tree) ? tree : tree ? [tree] : [];

  return (
    <div className="space-y-3">
      {/* Stats */}
      <div className="flex gap-3">
        {[
          { label: "节点数", value: String(nodeCount), icon: "ri-node-tree" },
          { label: "树深度", value: String(maxDepth + 1), icon: "ri-stack-line" },
        ].map(({ label, value, icon }) => (
          <div key={label} className="flex items-center gap-2 bg-[#F4F8FB] rounded-lg px-3 py-2">
            <i className={`${icon} text-[#0EA5A0] text-sm`} />
            <span className="text-xs text-[#718096]">{label}</span>
            <span className="text-sm font-semibold text-[#1A2B3C]">{value}</span>
          </div>
        ))}
      </div>

      {/* Tree */}
      <div className="max-h-[500px] overflow-y-auto">
        {roots.map((root, i) => (
          <TreeNodeCard key={root.id || root.node_id || i} node={root} />
        ))}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function FileDetailPage() {
  const { user } = useOutletContext<{ user: import("../../../services/auth").UserInfo | null }>();
  const { id } = useParams();
  const fileId = parseInt(id ?? "", 10);

  const [file, setFile] = useState<FileDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reindexing, setReindexing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [activeTab, setActiveTab] = useState<"info" | "tree" | "content" | "asr">("info");

  useEffect(() => {
    if (isNaN(fileId)) {
      setError("无效的文件 ID");
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    getFileDetail(fileId)
      .then((data) => {
        setFile(data);
        // Auto-select tree tab if index_tree exists
        if (data.index_tree) setActiveTab("tree");
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : "加载文件信息失败";
        setError(message);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [fileId]);

  const handleDownload = async () => {
    if (!file || downloading) return;
    if (!ensurePermission(user, "files:download", (msg) => alert(msg))) return;
    if (!(file.downloadable ?? false)) {
      alert("该文件未开放下载");
      return;
    }
    setDownloading(true);
    try {
      const { blob, filename } = await downloadFileContent(file.id, file.file_name);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err instanceof Error ? err.message : "下载失败");
    } finally {
      setDownloading(false);
    }
  };

  const handleReindex = () => {
    if (!ensurePermission(user, "files:write", (msg) => alert(msg))) return;
    if (reindexing || isNaN(fileId)) return;
    setReindexing(true);
    reindexFile(fileId)
      .then(() => {
        setFile((prev) => prev ? { ...prev, index_status: "processing" } : prev);
      })
      .catch((err: unknown) => {
        const message = err instanceof Error ? err.message : "重新索引请求失败";
        alert(message);
      })
      .finally(() => {
        setReindexing(false);
      });
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto flex items-center justify-center py-24">
        <div className="flex flex-col items-center gap-3 text-[#718096]">
          <RIcon name="loader-4-line" className="text-3xl animate-spin text-[#0EA5A0]" />
          <span className="text-sm">加载文件信息...</span>
        </div>
      </div>
    );
  }

  if (error || !file) {
    return (
      <div className="max-w-4xl mx-auto space-y-4">
        <nav className="flex items-center gap-1.5 text-sm">
          <Link to="/workbench/files" className="text-[#718096] hover:text-[#0EA5A0]">文件管理</Link>
          <span className="text-gray-300">/</span>
          <span className="text-[#1A2B3C] font-medium">文件详情</span>
        </nav>
        <div className="bg-white rounded-xl border border-gray-100 p-12 flex flex-col items-center gap-3 text-[#718096]">
          <RIcon name="error-warning-line" className="text-4xl text-red-400" />
          <p className="text-sm">{error ?? "文件不存在"}</p>
          <Link to="/workbench/files" className="mt-2 text-xs text-[#0EA5A0] hover:underline">返回文件列表</Link>
        </div>
      </div>
    );
  }

  const permissionLabel = file.data_permission_rule_name || permissionLabelMap[file.permission_type] || file.permission_type;
  const indexStatusLabel = getIndexStatusLabel(file.index_status);

  const indexStatusStyle: Record<string, { cls: string; icon: string }> = {
    已完成: { cls: "bg-emerald-100 text-emerald-700", icon: "ri-check-circle-line" },
    索引中: { cls: "bg-sky-100 text-sky-700", icon: "ri-loader-4-line animate-spin" },
    索引失败: { cls: "bg-red-100 text-red-600", icon: "ri-close-circle-line" },
  };

  const permissionColor: Record<string, string> = {
    "全公司": "bg-emerald-100 text-emerald-700",
    "指定部门": "bg-sky-100 text-sky-700",
    "指定工种": "bg-amber-100 text-amber-700",
    "指定用户": "bg-purple-100 text-purple-700",
  };

  const statusInfo = indexStatusStyle[indexStatusLabel] ?? indexStatusStyle["索引中"];

  const uploadTime = formatBeijingDate(file.created_at, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });

  const detailTabs: { id: "info" | "tree" | "content" | "asr"; label: string; icon: string; badge: string }[] = [
    { id: "info", label: "文件信息", icon: "ri-information-line", badge: "" },
    { id: "tree", label: "索引结构", icon: "ri-node-tree", badge: file.index_tree ? "1" : "" },
    { id: "content", label: "提取文本", icon: "ri-file-text-line", badge: file.content_text ? "1" : "" },
    ...(file.asr_result ? [{ id: "asr" as const, label: "语音/视觉识别", icon: "ri-eye-line", badge: "1" }] : []),
  ];

  return (
    <div className="max-w-4xl mx-auto space-y-4">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1.5 text-sm">
        <Link to="/workbench/files" className="text-[#718096] hover:text-[#0EA5A0]">文件管理</Link>
        <span className="text-gray-300">/</span>
        <span className="text-[#1A2B3C] font-medium truncate max-w-xs">{file.file_name}</span>
      </nav>

      {/* File Header Card */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 bg-[#F4F8FB] rounded-xl flex items-center justify-center flex-shrink-0">
            <RIcon name="file-pdf-line" className={`${ file.file_type === "pdf" ? " text-red-500" : ["word", "docx", "doc"].includes(file.file_type) ? " text-sky-500" : ["excel", "xlsx", "xls"].includes(file.file_type) ? " text-green-600" : ["mp4", "mp3", "wav"].includes(file.file_type) ? " text-purple-500" : " text-gray-400" } text-3xl`} />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold text-[#1A2B3C] mb-1 break-words">{file.file_name}</h1>
            <div className="flex flex-wrap gap-2">
              <span className={`text-xs px-2 py-1 rounded-full ${permissionColor[permissionLabel] ?? "bg-gray-100 text-gray-500"}`}>
                {permissionLabel}
              </span>
              <span className={`flex items-center gap-1 text-xs px-2 py-1 rounded-full ${statusInfo.cls}`}>
                <i className={statusInfo.icon} />{indexStatusLabel}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            {hasPermission(user, "files:download") && (file.downloadable ?? false) && (
              <button
                onClick={handleDownload}
                disabled={downloading}
                className="flex items-center gap-1.5 px-3 h-9 bg-[#0EA5A0] hover:bg-[#0D9590] text-white rounded-lg text-sm transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
              >
                <RIcon name="download-2-line" className={`${downloading ? "animate-pulse" : ""}`} />
                {downloading ? "下载中..." : "下载"}
              </button>
            )}
            <button
              onClick={handleReindex}
              disabled={reindexing}
              className="flex items-center gap-1.5 px-3 h-9 border border-gray-200 text-[#4A5568] rounded-lg text-sm hover:border-[#0EA5A0] hover:text-[#0EA5A0] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
            >
              <RIcon name="refresh-line" className={`${reindexing ? "animate-spin" : ""}`} />重新索引
            </button>
          </div>
        </div>
      </div>

      {/* Index Status Steps */}
      <div className="bg-white rounded-xl border border-gray-100 p-6">
        <h3 className="text-sm font-semibold text-[#1A2B3C] mb-4">索引流水线</h3>
        <div className="flex items-center gap-6">
          {[
            { label: "文件解析", done: true },
            { label: "文本提取", done: !!file.content_text },
            ...(file.file_type === "pdf" ? [{ label: "视觉理解", done: !!file.content_text && file.content_text.includes("视觉分析") }] : []),
            { label: "PageIndex 建树", done: !!file.index_tree },
            { label: "索引完成", done: indexStatusLabel === "已完成" },
          ].map((step, i, arr) => (
            <div key={i} className="flex items-center gap-2">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                step.done ? "bg-[#0EA5A0] text-white" : "bg-gray-100 text-gray-400"
              }`}>
                {step.done ? <RIcon name="check-line" className="text-xs" /> : <span className="text-xs">{i + 1}</span>}
              </div>
              <span className={`text-xs whitespace-nowrap ${step.done ? "text-[#0EA5A0] font-medium" : "text-[#718096]"}`}>{step.label}</span>
              {i < arr.length - 1 && <div className={`w-8 h-0.5 ${step.done ? "bg-[#0EA5A0]" : "bg-gray-200"}`} />}
            </div>
          ))}
        </div>
      </div>

      {/* Tabs + Content */}
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="flex border-b border-gray-100 px-2">
          {detailTabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors cursor-pointer ${
                activeTab === tab.id
                  ? "border-[#0EA5A0] text-[#0EA5A0]"
                  : "border-transparent text-[#718096] hover:text-[#4A5568]"
              }`}
            >
              <i className={tab.icon} />
              {tab.label}
              {tab.badge && (
                <span className="w-2 h-2 bg-[#0EA5A0] rounded-full" />
              )}
            </button>
          ))}
        </div>

        <div className="p-6">
          {/* File Info Tab */}
          {activeTab === "info" && (
            <div className="grid grid-cols-2 gap-x-8 gap-y-4">
              {[
                { label: "文件类型", value: file.file_type.toUpperCase() },
                { label: "文件大小", value: formatBytes(file.file_size) },
                { label: "MIME 类型", value: file.mime_type },
                { label: "上传时间", value: uploadTime },
                { label: "权限类型", value: permissionLabel },
                { label: "索引状态", value: indexStatusLabel },
                { label: "文件 ID", value: String(file.id) },
                { label: "所有者 ID", value: String(file.owner_id) },
              ].map(({ label, value }) => (
                <div key={label}>
                  <p className="text-xs text-[#718096] mb-0.5">{label}</p>
                  <p className="text-sm text-[#1A2B3C] font-medium">{value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Index Tree Tab */}
          {activeTab === "tree" && (
            <div>
              {file.index_tree ? (
                <IndexTreePanel treeJson={file.index_tree} />
              ) : (
                <div className="py-12 text-center">
                  <i className="ri-node-tree text-4xl text-gray-300 block mb-3" />
                  <p className="text-sm text-[#718096] mb-1">暂无索引结构</p>
                  <p className="text-xs text-[#718096]/60">
                    {indexStatusLabel === "索引中"
                      ? "PageIndex 正在构建索引树，请稍候..."
                      : "点击「重新索引」生成 PageIndex 索引树"
                    }
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Content Text Tab */}
          {activeTab === "content" && (
            <div>
              {file.content_text ? (
                <div className="max-h-[500px] overflow-y-auto">
                  <pre className="text-sm text-[#4A5568] leading-relaxed whitespace-pre-wrap font-sans">{file.content_text}</pre>
                </div>
              ) : (
                <div className="py-12 text-center">
                  <RIcon name="file-text-line" className="text-4xl text-gray-300 block mb-3" />
                  <p className="text-sm text-[#718096]">暂无提取文本</p>
                </div>
              )}
            </div>
          )}

          {/* ASR / VLM Tab */}
          {activeTab === "asr" && (
            <div>
              {file.asr_result ? (
                <div className="space-y-3">
                  {file.asr_status && (
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-[#718096]">识别状态：</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        file.asr_status === "completed" ? "bg-emerald-100 text-emerald-700" :
                        file.asr_status === "processing" ? "bg-sky-100 text-sky-700" :
                        "bg-gray-100 text-gray-500"
                      }`}>
                        {getAsrStatusLabel(file.asr_status)}
                      </span>
                    </div>
                  )}
                  <div className="max-h-[500px] overflow-y-auto">
                    <pre className="text-sm text-[#4A5568] leading-relaxed whitespace-pre-wrap font-sans">{file.asr_result}</pre>
                  </div>
                </div>
              ) : (
                <div className="py-12 text-center">
                  <RIcon name="eye-line" className="text-4xl text-gray-300 block mb-3" />
                  <p className="text-sm text-[#718096]">暂无识别结果</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
